import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  ScreenLayout,
  Modal,
  Avatar,
  Button,
  Input,
  StatusBadge,
  IconRabbit,
  IconCheck,
  IconSyringe,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  ADOPTION_STATUSES,
  CARE_TYPES,
  CHARACTER_OPTIONS,
  adoptionStatusOf,
  assetToCat,
  careRecordToCare,
  catToCustomFields,
  todayIso,
  type AdoptionStatus,
  type AppTab,
  type Care,
  type CareType,
  type Cat,
} from './data.js';
import { CatDetailSkeleton } from './skeletons.js';

/**
 * Fiche d'un chat (ecran de detail).
 *
 * Regles de la cage : data UNIQUEMENT via @taki/sdk (useTakiApp), UI UNIQUEMENT
 * via @taki/ui, un seul ScreenLayout a la racine avec les etats standard
 * (chargement / erreur) branches. Zero couleur hex en dur. Le contenu de la
 * zone 5 est A PLAT, sans Card (regle G9) ; les formulaires de modification et
 * d'ajout de soin s'ouvrent dans le Modal standard (regle G6).
 *
 * Le chat est charge via sdk.assets.get, ses soins via sdk.careRecords.list()
 * filtres par assetId. On peut : changer le statut d'adoption en un clic,
 * modifier la fiche, et ajouter un soin. Les mises a jour passent par
 * sdk.assets.update (patch partiel ; `orgId` et `type` sont immuables).
 */
export interface CatDetailScreenProps {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  catId: string;
  onBack: () => void;
}

/** Trie les soins du plus recent au plus ancien (date ISO comparable en chaine). */
function byDateDesc(a: Care, b: Care): number {
  if (a.date < b.date) return 1;
  if (a.date > b.date) return -1;
  return 0;
}

export function CatDetailScreen({
  activeTab,
  onTabChange,
  catId,
  onBack,
}: CatDetailScreenProps): JSX.Element {
  const { sdk, config } = useTakiApp();

  const [cat, setCat] = useState<Cat | null>(null);
  const [cares, setCares] = useState<Care[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Changement de statut (persiste via sdk.assets.update).
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Modal ouvert : un seul a la fois par ecran (doctrine G6), modification de
  // la fiche ou ajout d'un soin.
  const [openModal, setOpenModal] = useState<'edit' | 'care' | null>(null);

  // Formulaire de modification de la fiche.
  const [eName, setEName] = useState('');
  const [eCharacter, setECharacter] = useState<string[]>([]);
  const [eBirthYear, setEBirthYear] = useState('');
  const [eDiet, setEDiet] = useState('');
  const [ePhoto, setEPhoto] = useState('');
  const [eVaccines, setEVaccines] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Formulaire d'ajout de soin.
  const [careType, setCareType] = useState<CareType>('vaccination');
  const [dateStr, setDateStr] = useState(todayIso);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Libelles caches (value -> label) pour les caracteres et les types de soin.
  const characterLabel = useMemo(() => {
    const map = new Map(CHARACTER_OPTIONS.map((o) => [o.value, o.label]));
    return (value: string): string => map.get(value) ?? value;
  }, []);
  const careTypeLabel = useMemo(() => {
    const map = new Map(CARE_TYPES.map((o) => [o.value, o.label]));
    return (value: CareType): string => map.get(value) ?? value;
  }, []);

  const loadCares = useCallback(async () => {
    const records = await sdk.careRecords.list();
    const mapped = records
      .filter((record) => record.assetId === catId)
      .map((record) => careRecordToCare(record));
    mapped.sort(byDateDesc);
    setCares(mapped);
  }, [sdk, catId]);

  const refresh = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const asset = await sdk.assets.get(catId);
      if (asset == null) {
        setCat(null);
        setListError('Chat introuvable.');
        return;
      }
      setCat(assetToCat(asset));
      await loadCares();
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Echec du chargement.');
    } finally {
      setLoadingList(false);
    }
  }, [sdk, catId, loadCares]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Change le statut d'adoption et le PERSISTE (sac customFields complet). */
  const handleStatusChange = useCallback(
    async (next: AdoptionStatus) => {
      if (cat == null || next === cat.adoptionStatus) return;
      setStatusSaving(true);
      setStatusError(null);
      try {
        const updated = await sdk.assets.update(catId, {
          customFields: catToCustomFields({ ...cat, adoptionStatus: next }),
        });
        setCat(assetToCat(updated));
      } catch (err) {
        setStatusError(err instanceof Error ? err.message : 'Echec du changement de statut.');
      } finally {
        setStatusSaving(false);
      }
    },
    [sdk, catId, cat],
  );

  /** Ouvre le modal de modification, pre-rempli avec la fiche courante. */
  const openEdit = useCallback(() => {
    if (cat == null) return;
    setEName(cat.name);
    setECharacter(cat.character);
    setEBirthYear(cat.birthYear == null ? '' : String(cat.birthYear));
    setEDiet(cat.diet);
    setEPhoto(cat.photoUrl);
    setEVaccines(cat.vaccinesUpToDate);
    setEditError(null);
    setOpenModal('edit');
  }, [cat]);

  /** Ouvre le modal d'ajout de soin. */
  const openCare = useCallback(() => {
    setSaveError(null);
    setOpenModal('care');
  }, []);

  /** Ferme le modal ouvert (modification ou soin). */
  const closeModal = useCallback(() => {
    setOpenModal(null);
  }, []);

  const toggleEditCharacter = useCallback((value: string) => {
    setECharacter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (cat == null) return;
    const name = eName.trim();
    if (name === '') {
      setEditError('Le nom du chat est obligatoire.');
      return;
    }
    const yearStr = eBirthYear.trim();
    const year = yearStr === '' ? null : Number.parseInt(yearStr, 10);
    if (year !== null && Number.isNaN(year)) {
      setEditError('Annee de naissance invalide.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await sdk.assets.update(catId, {
        name,
        customFields: catToCustomFields({
          ...cat,
          character: eCharacter,
          birthYear: year,
          diet: eDiet.trim(),
          photoUrl: ePhoto.trim(),
          vaccinesUpToDate: eVaccines,
        }),
      });
      setCat(assetToCat(updated));
      setOpenModal(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Echec de l'enregistrement.");
    } finally {
      setEditSaving(false);
    }
  }, [sdk, catId, cat, eName, eCharacter, eBirthYear, eDiet, ePhoto, eVaccines]);

  const handleSaveCare = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await sdk.careRecords.create({
        orgId: config.orgId,
        type: careType,
        assetId: catId,
        performedAt: new Date(dateStr),
        customFields: { description: description.trim() },
      });
      setOpenModal(null);
      setDescription('');
      setCareType('vaccination');
      setDateStr(todayIso());
      await loadCares();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Echec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  }, [sdk, config.orgId, careType, catId, dateStr, description, loadCares]);

  const statusMeta = cat != null ? adoptionStatusOf(cat.adoptionStatus) : null;

  return (
    <ScreenLayout
      appName="Cat Manager Six"
      appIcon={<IconRabbit size={18} />}
      menuItems={[
        { id: 'cats', label: 'Les chats' },
        { id: 'visits', label: 'Les visites' },
      ]}
      activeMenuId={activeTab}
      onMenuSelect={(id) => onTabChange(id as AppTab)}
      /* Zone 2 : le h1 affiche le label de l'onglet actif ("Les chats") ;
         `title` reste le repli si l'onglet actif venait a manquer. L'identite
         du chat (nom, statut) vit dans le contenu de la zone 5. */
      title={cat?.name ?? 'Fiche du chat'}
      quickActions={[
        {
          id: 'edit-cat',
          label: 'Modifier la fiche',
          variant: 'secondary',
          disabled: loadingList || listError != null,
          onClick: openEdit,
        },
        // Contrat v2 : l'action d'ajout (ici un soin) se declare `kind: 'add'`,
        // le gabarit impose "Ajouter" en premier, IconPlus et le lime (G5).
        {
          id: 'add-care',
          label: 'Ajouter',
          kind: 'add',
          disabled: loadingList || listError != null,
          onClick: openCare,
        },
      ]}
      loading={loadingList && cat == null}
      skeleton={<CatDetailSkeleton />}
      error={listError}
      errorTitle="Echec du chargement"
      onRetry={() => void refresh()}
      retryLabel="Reessayer"
    >
      {cat == null ? null : (
        <div className="pb-8">
          {/* Retour vers la liste */}
          <div className="px-5 pt-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              Retour
            </Button>
          </div>

          {/* Profil : a plat, pleine largeur, sans Card (regle G9). */}
          <section className="border-b border-border-soft px-5 pb-5 pt-4">
            <h3 className="pb-3 text-sm font-semibold text-ink-900">Profil</h3>
            <div className="flex flex-col gap-4">
              {/* Photo et statut actuel */}
              <div className="flex items-center gap-4">
                <Avatar size={64} initials={cat.name.slice(0, 2)}>
                  {cat.photoUrl ? (
                    <img
                      src={cat.photoUrl}
                      alt={cat.name}
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : undefined}
                </Avatar>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-ink-500">Statut</span>
                  {statusMeta != null && (
                    <span>
                      <StatusBadge tone={statusMeta.tone} label={statusMeta.label} />
                    </span>
                  )}
                </div>
              </div>

              {/* Changement de statut en un clic (persiste) */}
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-ink-500">Changer le statut</span>
                <div className="flex flex-wrap gap-2">
                  {ADOPTION_STATUSES.map((status) => (
                    <Button
                      key={status.value}
                      size="sm"
                      variant={status.value === cat.adoptionStatus ? 'primary' : 'secondary'}
                      disabled={statusSaving}
                      onClick={() => void handleStatusChange(status.value)}
                    >
                      {status.label}
                    </Button>
                  ))}
                </div>
                {statusError != null && (
                  <p className="text-xs text-danger">{statusError}</p>
                )}
              </div>

              {/* Caracteres */}
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-ink-500">Caractere</span>
                {cat.character.length === 0 ? (
                  <span className="text-sm text-ink-500">Sans caractere</span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {cat.character.map((trait) => (
                      <Button key={trait} size="sm" variant="secondary">
                        {characterLabel(trait)}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sante et quotidien : vaccins, annee de naissance, regime */}
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-ink-500">Vaccins</span>
                  {cat.vaccinesUpToDate ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-ink-900">
                      <IconCheck size={16} className="text-accent" />
                      A jour
                    </span>
                  ) : (
                    <span className="text-sm text-ink-900">A verifier</span>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-ink-500">Annee de naissance</span>
                  <span className="text-sm text-ink-900">{cat.birthYear ?? 'Inconnue'}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-ink-500">Regime</span>
                  <span className="text-sm text-ink-900">{cat.diet || 'Non renseigne'}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Soins : historique a plat, l'ajout passe par le modal (regles G6 et G9). */}
          <section className="border-b border-border-soft px-5 pb-5 pt-4">
            <h3 className="pb-3 text-sm font-semibold text-ink-900">Soins</h3>
            {cares.length === 0 ? (
              <p className="text-sm text-ink-500">Aucun soin enregistre.</p>
            ) : (
              <ul className="flex flex-col">
                {cares.map((care) => (
                  <li
                    key={care.id}
                    className="flex items-start gap-3 border-b border-border-soft py-2.5 last:border-b-0"
                  >
                    <IconSyringe size={16} className="mt-0.5 shrink-0 text-ink-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink-900">
                        {careTypeLabel(care.type)}
                      </p>
                      {care.description ? (
                        <p className="text-xs text-ink-500">{care.description}</p>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-xs text-ink-400">{care.date}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {/* Modal de modification de la fiche : pop-up centrale standard (regle G6). */}
      <Modal
        open={openModal === 'edit'}
        onClose={closeModal}
        title="Modifier la fiche"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={editSaving} onClick={closeModal}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={editSaving}
              onClick={() => void handleSaveEdit()}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-name" className="text-xs font-medium text-ink-500">
              Nom
            </label>
            <Input
              id="edit-name"
              type="text"
              className="h-10"
              value={eName}
              onChange={(event) => setEName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Caractere</span>
            <div className="flex flex-wrap gap-2">
              {CHARACTER_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={eCharacter.includes(option.value) ? 'primary' : 'secondary'}
                  onClick={() => toggleEditCharacter(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Vaccins</span>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={eVaccines ? 'primary' : 'secondary'}
                onClick={() => setEVaccines(true)}
              >
                A jour
              </Button>
              <Button
                size="sm"
                variant={eVaccines ? 'secondary' : 'primary'}
                onClick={() => setEVaccines(false)}
              >
                A verifier
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="edit-year" className="text-xs font-medium text-ink-500">
                Annee de naissance
              </label>
              <Input
                id="edit-year"
                type="number"
                className="h-10"
                value={eBirthYear}
                placeholder="Ex. 2021"
                onChange={(event) => setEBirthYear(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="edit-diet" className="text-xs font-medium text-ink-500">
                Regime alimentaire
              </label>
              <Input
                id="edit-diet"
                type="text"
                className="h-10"
                value={eDiet}
                placeholder="Optionnel"
                onChange={(event) => setEDiet(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-photo" className="text-xs font-medium text-ink-500">
              Lien de la photo
            </label>
            <Input
              id="edit-photo"
              type="text"
              className="h-10"
              value={ePhoto}
              placeholder="Optionnel"
              onChange={(event) => setEPhoto(event.target.value)}
            />
          </div>

          {editError != null && <p className="text-xs text-danger">{editError}</p>}
        </div>
      </Modal>

      {/* Modal d'ajout de soin : pop-up centrale standard (regle G6). */}
      <Modal
        open={openModal === 'care'}
        onClose={closeModal}
        title="Ajouter un soin"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeModal}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleSaveCare()}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Type de soin</span>
            <div className="flex flex-wrap gap-2">
              {CARE_TYPES.map((type) => (
                <Button
                  key={type.value}
                  size="sm"
                  variant={type.value === careType ? 'primary' : 'secondary'}
                  onClick={() => setCareType(type.value)}
                >
                  {type.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="care-date" className="text-xs font-medium text-ink-500">
              Date
            </label>
            <Input
              id="care-date"
              type="date"
              className="h-10"
              value={dateStr}
              onChange={(event) => setDateStr(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="care-desc" className="text-xs font-medium text-ink-500">
              Description
            </label>
            <Input
              id="care-desc"
              type="text"
              className="h-10"
              value={description}
              placeholder="Ex. Rappel vaccin annuel"
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>
    </ScreenLayout>
  );
}

export default CatDetailScreen;
